import { Form, Formik } from 'formik';
import * as bd from 'react-basic-design';
import { UserProfileDTO } from '../../../api/user-profile-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDelete from '../../../assets/icons/Delete';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { T } from '../../../components/basic/text';
import { yup } from '../../../components/basic/yup';
import { FormikTextField } from '../../../components/formik';
import { Toggle } from 'react-basic-design';

interface SaveUserProfileModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedUserProfile?: UserProfileDTO;
    onHide: () => void;
    onSave: (values: UserProfileDTO) => void;
    onDelete?: (profile: UserProfileDTO) => void;
}

export function SaveUserProfileModal({
    show,
    allowEdit,
    selectedUserProfile,
    onHide,
    onSave,
    onDelete,
}: SaveUserProfileModalProps) {
    const UserProfileSchema = yup.object().shape({
        userName: yup.string().required(' ').max(50),
        fullName: yup.string().max(255),
        company: yup.string().max(255),
        phoneNumber: yup.string().max(50),
        city: yup.string().max(100),
        country: yup.string().max(100),
        postalCode: yup.string().max(20),
    });

    const defaultValues: UserProfileDTO = {
        id: selectedUserProfile?.id || 0,
        userName: selectedUserProfile?.userName || '',
        fullName: selectedUserProfile?.fullName || '',
        company: selectedUserProfile?.company || '',
        phoneNumber: selectedUserProfile?.phoneNumber || '',
        address: selectedUserProfile?.address || '',
        city: selectedUserProfile?.city || '',
        country: selectedUserProfile?.country || '',
        postalCode: selectedUserProfile?.postalCode || '',
        smsNotifications: selectedUserProfile?.smsNotifications || false,
        createdAt: selectedUserProfile?.createdAt || new Date().toISOString(),
        updatedAt: selectedUserProfile?.updatedAt,
    };

    if (!show) return <></>;

    return (
        <Formik initialValues={defaultValues} validationSchema={UserProfileSchema} onSubmit={onSave}>
            {formik => (
                <BasicModal
                    show={show}
                    onHide={onHide}
                    size="xl"
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <T>{selectedUserProfile ? 'edit-user-profile' : 'new-user-profile'}</T>
                            <bd.Flex align="center" gap={2}>
                                <bd.Button variant="icon" size="sm" onClick={formik.submitForm} disabled={!allowEdit}>
                                    <SvgSave />
                                </bd.Button>
                                {selectedUserProfile && onDelete && (
                                    <bd.Button
                                        disabled={!allowEdit}
                                        variant="icon"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => onDelete(selectedUserProfile)}
                                    >
                                        <SvgDelete />
                                    </bd.Button>
                                )}
                                <bd.Button variant="icon" color="secondary" size="sm" onClick={onHide}>
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex align="center" wrap gap={2}>
                            <FormikTextField
                                label="username"
                                name="userName"
                                readOnly={!allowEdit || !!selectedUserProfile}
                                width="15rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="full-name"
                                name="fullName"
                                readOnly={!allowEdit}
                                width="15rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="company"
                                name="company"
                                readOnly={!allowEdit}
                                width="15rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="phone-number"
                                name="phoneNumber"
                                readOnly={!allowEdit}
                                width="15rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="city"
                                name="city"
                                readOnly={!allowEdit}
                                width="15rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="country"
                                name="country"
                                readOnly={!allowEdit}
                                width="15rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="postal-code"
                                name="postalCode"
                                readOnly={!allowEdit}
                                width="15rem"
                                handleEnter
                            />

                            <div className="d-flex align-items-center gap-2">
                                <label className="form-label mb-0">
                                    <T>sms-notifications</T>
                                </label>
                                <Toggle
                                    checked={formik.values.smsNotifications}
                                    onChange={v => formik.setFieldValue('smsNotifications', v)}
                                    disabled={!allowEdit}
                                />
                            </div>
                        </bd.Flex>

                        <FormikTextField
                            type="textarea"
                            label="address"
                            name="address"
                            readOnly={!allowEdit}
                            className="w-100 mt-3"
                            handleEnter
                        />
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
}

